import React, { createContext, useContext, useState, useCallback } from "react";

const CartContext = createContext();

export const useCart = () => {
	const context = useContext(CartContext);
	if (!context) {
		throw new Error("useCart must be used within a CartProvider");
	}
	return context;
};

export const CartProvider = ({ children }) => {
	const [items, setItems] = useState([]);
	const [isCartOpen, setIsCartOpen] = useState(false);

	const addItem = useCallback((product, quantity = 1) => {
		setItems((prev) => {
			const existing = prev.find((item) => item.id === product.id);
			if (existing) {
				return prev.map((item) =>
					item.id === product.id
						? { ...item, quantity: item.quantity + quantity }
						: item
				);
			}
			return [...prev, { ...product, quantity }];
		});
		setIsCartOpen(true);
	}, []);

	const removeItem = useCallback((productId) => {
		setItems((prev) => prev.filter((item) => item.id !== productId));
	}, []);

	const updateQuantity = useCallback((productId, quantity) => {
		if (quantity <= 0) {
			setItems((prev) => prev.filter((item) => item.id !== productId));
			return;
		}
		setItems((prev) =>
			prev.map((item) =>
				item.id === productId ? { ...item, quantity } : item
			)
		);
	}, []);

	const clearCart = useCallback(() => {
		setItems([]);
	}, []);

	const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
	const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);

	return (
		<CartContext.Provider
			value={{
				items,
				itemCount,
				subtotal,
				isCartOpen,
				setIsCartOpen,
				addItem,
				removeItem,
				updateQuantity,
				clearCart,
			}}
		>
			{children}
		</CartContext.Provider>
	);
};
